import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { BrandLogo } from "@/components/BrandLogo";

const formatDetail = (d) => {
  if (d == null) return "Something went wrong.";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  if (typeof d === "object" && typeof d.msg === "string") return d.msg;
  return String(d);
};

export default function LoginPage() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const u = await login(email.trim(), password);
      if (u?.must_change_password) {
        nav("/set-password");
      } else {
        nav("/dashboard");
      }
    } catch (err) {
      setError(
        formatDetail(err?.response?.data?.detail) ||
          err?.message ||
          "Sign-in failed. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen aa-surface flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        {/* Brand block */}
        <div className="flex flex-col items-center mb-10">
          <BrandLogo surface="light" height={56} withOpsMarker={false} testid="login-brand" />
          <div className="mt-3 text-[13px] text-steel tracking-wide">
            Operations Platform
          </div>
        </div>

        {/* Card */}
        <div
          data-testid="login-card"
          className="bg-white border border-coach-silver shadow-sm rounded-md overflow-hidden"
        >
          <div className="bg-carbon text-white px-6 py-3 text-[12px] tracking-widest uppercase">
            Sign in
          </div>
          <form onSubmit={submit} className="p-6 space-y-4">
            <div>
              <label className="block text-[12px] font-medium text-carbon mb-1.5 tracking-wide">
                Email
              </label>
              <input
                data-testid="login-email"
                type="email"
                autoComplete="username"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full border border-coach-silver rounded-sm px-3 py-2 text-[14px] focus:outline-none focus:ring-2 focus:ring-coach-blue"
                placeholder="you@allaccess.test"
              />
            </div>
            <div>
              <label className="block text-[12px] font-medium text-carbon mb-1.5 tracking-wide">
                Password
              </label>
              <input
                data-testid="login-password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full border border-coach-silver rounded-sm px-3 py-2 text-[14px] focus:outline-none focus:ring-2 focus:ring-coach-blue"
                placeholder="••••••••"
              />
            </div>

            {error && (
              <div
                data-testid="login-error"
                className="text-[12px] text-critical-red bg-critical-red/5 border border-critical-red/30 rounded-sm px-3 py-2"
              >
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit"
              className="w-full bg-coach-blue hover:bg-deep-road-navy transition-colors text-white text-[13px] font-medium tracking-wide py-2.5 rounded-sm disabled:opacity-60"
            >
              {loading ? "Signing in…" : "Sign in"}
            </button>
          </form>
          <div className="border-t border-coach-silver bg-road-white px-6 py-3 text-[11px] text-steel text-center">
            Internal Operations Platform — Authorized Personnel Only
          </div>
        </div>
      </div>
    </div>
  );
}
